/**
 * Which routes the signed-in principal may open, and where to send them when
 * they may not.
 *
 * Every rule is a grant now. Recording a sale is `sale · create`, the invoice
 * statement is `situation · read · invoice`, tracing stock is
 * `stock_tracing · read`; the admin portal and settings are virtual resources
 * in the same grant registry.
 *
 * Rules are a predicate rather than a name so a route can ask for a modifier
 * (`sale · read · print_receipt`) or for any one of several things, which the
 * flat name list could not express.
 */

import {
	AccessPolicy,
	canRecord,
	canSee,
	canSeeAny,
	canSeeDashboard,
	canSeeLiveStock,
	canSeeSituation,
	canSeeStockTimeline,
	canTraceStock,
	canRead,
} from './access';
import {
	canAccessCashHub,
	canAccessPeopleHub,
	canAccessReportsHub,
	canAccessStockHub,
} from '../features/hubs/hubAccess';

/** Everything a rule may consult: the access grants. */
export type AccessContext = {
	access: AccessPolicy | null | undefined;
};

export type RoutePermissionRule = {
	/** Path prefix (longest match wins). */
	path: string;
	/** Why it is named, for anyone reading the table. */
	grant: string;
	/** True when this principal may open it. */
	allow: (ctx: AccessContext) => boolean;
};

const read = (posting: string) => (ctx: AccessContext) => canSee(ctx.access, posting);
const open = () => true;

/** Longest-prefix rules. Keep in sync with main.tsx TAB_ROUTES / TAB_GRANTS. */
export const ROUTE_PERMISSION_RULES: RoutePermissionRule[] = [
	{ path: '/admin_settings', grant: 'settings · read', allow: (c) => canRead(c.access, 'settings') },
	{ path: '/settings', grant: 'any account', allow: open },
	{ path: '/dashboard', grant: 'dashboard · read', allow: (c) => canSeeDashboard(c.access) },
	{ path: '/pos/terminals', grant: 'sale · create', allow: (c) => canRecord(c.access, 'sale') },
	{ path: '/pos', grant: 'sale · create', allow: (c) => canRecord(c.access, 'sale') },
	{ path: '/invoice/situation', grant: 'situation · read · invoice', allow: (c) => canSeeSituation(c.access, 'invoice') },
	{ path: '/payments/mode', grant: 'payment · read', allow: read('payment') },
	{ path: '/payments/type', grant: 'payment · read', allow: read('payment') },
	{ path: '/payments/user', grant: 'payment · read', allow: read('payment') },
	{ path: '/payments/depot', grant: 'payment · read', allow: read('payment') },
	{ path: '/payments', grant: 'payment · read', allow: read('payment') },
	{ path: '/supply-payments/mode', grant: 'supply_payment · read', allow: read('supply_payment') },
	{ path: '/supply-payments/user', grant: 'supply_payment · read', allow: read('supply_payment') },
	{ path: '/supply-payments/depot', grant: 'supply_payment · read', allow: read('supply_payment') },
	{ path: '/supply-payments', grant: 'supply_payment · read', allow: read('supply_payment') },
	// Sales and returns are two postings now; these screens show both.
	{ path: '/sales/item', grant: 'sale|return · read', allow: (c) => canSeeAny(c.access, ['sale', 'return']) },
	{ path: '/sales/family', grant: 'sale|return · read', allow: (c) => canSeeAny(c.access, ['sale', 'return']) },
	{ path: '/sales/category', grant: 'sale|return · read', allow: (c) => canSeeAny(c.access, ['sale', 'return']) },
	{ path: '/sales/depot', grant: 'sale|return · read', allow: (c) => canSeeAny(c.access, ['sale', 'return']) },
	{ path: '/sales/sector', grant: 'sale|return · read', allow: (c) => canSeeAny(c.access, ['sale', 'return']) },
	{ path: '/sales/user', grant: 'sale|return · read', allow: (c) => canSeeAny(c.access, ['sale', 'return']) },
	{ path: '/sales', grant: 'sale|return · read', allow: (c) => canSeeAny(c.access, ['sale', 'return']) },
	{ path: '/expenses', grant: 'expense · read', allow: read('expense') },
	{ path: '/vouchers', grant: 'voucher · read', allow: read('voucher') },
	{ path: '/offers', grant: 'offer · read', allow: read('offer') },
	{ path: '/clients/wallets/transactions', grant: 'wallet · read', allow: read('wallet') },
	{ path: '/clients/wallets', grant: 'wallet · read', allow: read('wallet') },
	{ path: '/clients/addresses', grant: 'client_address (table) · read', allow: (c) => canRead(c.access, 'client_address') },
	{ path: '/clients', grant: 'situation · read · client', allow: (c) => canSeeSituation(c.access, 'client') },
	{ path: '/agents', grant: 'situation · read · agent', allow: (c) => canSeeSituation(c.access, 'agent') },
	{ path: '/suppliers', grant: 'situation · read · supplier', allow: (c) => canSeeSituation(c.access, 'supplier') },
	{ path: '/deposits/pending', grant: 'deposit · read', allow: (c) => canSee(c.access, 'deposit') || canRecord(c.access, 'deposit') },
	{ path: '/deposits', grant: 'deposit · read', allow: (c) => canSee(c.access, 'deposit') || canRecord(c.access, 'deposit') },
	// Tracing and the chronological view are separate grants on the server.
	{ path: '/stock/movement/trace/chrono', grant: 'stock_timeline · read', allow: (c) => canSeeStockTimeline(c.access) },
	{ path: '/stock/movement/trace', grant: 'stock_tracing · read', allow: (c) => canTraceStock(c.access) },
	{ path: '/stock/movement/list', grant: 'movement|discard · read', allow: (c) => canSeeAny(c.access, ['movement', 'discard']) },
	{ path: '/stock/movement', grant: 'movement|discard · read', allow: (c) => canSeeAny(c.access, ['movement', 'discard']) },
	{ path: '/stock/supply/situation', grant: 'situation · read · supply', allow: (c) => canSeeSituation(c.access, 'supply') },
	{ path: '/stock/supply/list', grant: 'supply · read', allow: read('supply') },
	{ path: '/stock/supply', grant: 'supply · read', allow: read('supply') },
	{ path: '/stock/inventory/current/visual', grant: 'inventory_current · read', allow: (c) => canSeeLiveStock(c.access) },
	{ path: '/stock/inventory/current', grant: 'inventory_current · read', allow: (c) => canSeeLiveStock(c.access) },
	// The product catalogue is the item table, not a stock figure.
	{ path: '/stock/inventory/product-catalog', grant: 'item · read', allow: (c) => canRead(c.access, 'item') },
	{ path: '/stock/inventory', grant: 'inventory · read', allow: read('inventory') },
	{ path: '/stock/conversion', grant: 'conversion · read', allow: read('conversion') },
	// A bill of materials is a table of its own now, not part of production.
	{ path: '/stock/production/boms', grant: 'prod_bom · read', allow: (c) => canRead(c.access, 'prod_bom') },
	{ path: '/stock/production', grant: 'production · read', allow: read('production') },
	{ path: '/people', grant: 'any People screen', allow: (c) => canAccessPeopleHub(c.access) },
	{ path: '/cash', grant: 'any Cash screen', allow: (c) => canAccessCashHub(c.access) },
	{ path: '/stock', grant: 'any Stock screen', allow: (c) => canAccessStockHub(c.access) },
	{ path: '/reports', grant: 'any Reports screen', allow: (c) => canAccessReportsHub(c.access) },
	{ path: '/login', grant: 'any account', allow: open },
];

const SORTED_RULES = [...ROUTE_PERMISSION_RULES].sort(
	(a, b) => b.path.length - a.path.length
);

const ANY_ACCOUNT: RoutePermissionRule = { path: '/', grant: 'any account', allow: open };

function matchRule(pathname: string): RoutePermissionRule {
	const path = pathname || '/';
	if (path === '/' || path === '') return ANY_ACCOUNT;
	for (const rule of SORTED_RULES) {
		if (path === rule.path || path.startsWith(`${rule.path}/`)) return rule;
	}
	return { ...ANY_ACCOUNT, path };
}

/** What a route asks for, for a message or a tooltip. */
export const grantForPath = (pathname: string): string => matchRule(pathname).grant;

export function canAccessPath(pathname: string, ctx: AccessContext): boolean {
	return matchRule(pathname).allow(ctx);
}

/** First safe landing path for the current grants. */
export function getDefaultPathForAccess(ctx: AccessContext): string {
	const { access } = ctx;
	if (canSeeDashboard(access)) return '/dashboard';
	if (canRecord(access, 'sale')) return '/pos/terminals';
	if (canSeeSituation(access, 'invoice')) return '/invoice/situation';
	if (canSee(access, 'payment')) return '/payments';
	if (canSeeAny(access, ['sale', 'return'])) return '/sales';
	if (canAccessPeopleHub(access)) return '/people';
	if (canAccessCashHub(access)) return '/cash';
	if (canAccessStockHub(access)) return '/stock';
	if (canSeeSituation(access, 'client')) return '/clients';
	if (canSee(access, 'movement')) return '/stock/movement';
	if (canSee(access, 'supply')) return '/stock/supply';
	if (canSee(access, 'inventory')) return '/stock/inventory';
	if (canSeeLiveStock(access)) return '/stock/inventory/current';
	if (canAccessReportsHub(access)) return '/reports';
	if (canRead(access, 'settings')) return '/admin_settings';
	return '/settings';
}
